import * as React from "react";
import MuiButton from "@mui/material/Button";
import type { ButtonProps as MuiButtonProps } from "@mui/material/Button";
import { cn } from "../../lib/utils";

export interface ButtonProps extends Omit<MuiButtonProps, "variant" | "color" | "size"> {
  variant?: "default" | "primary" | "secondary" | "outline" | "ghost" | "link" | "light";
  size?: "sm" | "md" | "lg" | "icon";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", sx, ...props }, ref) => {
    const v = variant === "default" ? "primary" : variant;

    const muiVariant =
      v === "outline"
        ? "outlined"
        : v === "ghost" || v === "link"
          ? "text"
          : "contained";

    let muiColor: MuiButtonProps["color"] = "primary";
    if (v === "secondary") muiColor = "secondary";
    if (v === "outline" || v === "ghost" || v === "light") muiColor = "inherit";
    if (v === "link") muiColor = "primary";

    const muiSize = size === "sm" ? "small" : size === "lg" ? "large" : "medium";

    const sizeSx =
      size === "sm"
        ? { minHeight: 36, px: 1.5, fontSize: "0.875rem" }
        : size === "md"
          ? { minHeight: 44, px: 2.5, fontSize: "1rem" }
          : size === "lg"
            ? { minHeight: 56, px: 3, fontSize: "1.125rem", fontWeight: 600 }
            : { minHeight: 40, width: 40, minWidth: 40, p: 0 };

    const variantSx =
      v === "outline"
        ? {
            color: "#0f172a",
            borderColor: "#cbd5e1",
            "&:hover": {
              borderColor: "#94a3b8",
              backgroundColor: "rgba(248, 250, 252, 0.95)",
            },
          }
        : v === "ghost"
          ? {
              color: "#334155",
              "&:hover": { backgroundColor: "rgba(241, 245, 249, 0.95)" },
            }
          : v === "link"
            ? {
                color: "#1d4ed8",
                textDecoration: "underline",
                textUnderlineOffset: "4px",
                minWidth: "auto",
                p: 0,
                fontWeight: 500,
                "&:hover": { backgroundColor: "transparent" },
              }
            : v === "light"
              ? {
                  backgroundColor: "#ffffff",
                  color: "#020617",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#e2e8f0" },
                }
              : {};

    return (
      <MuiButton
        ref={ref}
        variant={muiVariant}
        color={muiColor}
        size={muiSize}
        disableElevation={v === "primary" || v === "secondary" || v === "light"}
        className={cn(className)}
        sx={{
          textTransform: "none",
          borderRadius: "6px",
          fontWeight: 500,
          boxShadow:
            v === "primary" || v === "secondary"
              ? "0 1px 2px 0 rgb(15 23 42 / 0.05)"
              : v === "light"
                ? "none"
                : "none",
          ...sizeSx,
          ...variantSx,
          ...(sx as object),
        }}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button };
